import { PRODUCT_NAME } from "@mailhand/core/brand";
import { useState } from "react";
import { ACTION_TYPE_LABEL } from "../../app/labels.js";
import { type ActionNewOrigin, Link, navigate } from "../../app/router.js";
import { useApi } from "../../app/services.js";
import { useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { useResource } from "../../lib/useResource.js";
import { Badge, ErrorNote, Eyebrow, Icon, PageHead, Spinner } from "../../ui/index.js";
import { ActionForm, type ActionFormSubmit } from "./ActionForm.js";
import { type ActionDraft, type RunnableType, draftFromTemplate, emptyDraft } from "./draft.js";

const TYPE_CHOICES: readonly { type: RunnableType; blurb: string }[] = [
  { type: "reply", blurb: `${PRODUCT_NAME} writes an answer from your instructions and sends it.` },
  { type: "forward", blurb: "Send the email to a person or a team address." },
  { type: "webhook", blurb: "Call your own system with the details it found." },
];

const ORIGIN: Record<ActionNewOrigin, { path: string; label: string }> = {
  actions: { path: "/actions", label: "Actions" },
  setup: { path: "/setup", label: "Setup" },
};

export function NewActionScreen({ from }: { from: ActionNewOrigin }) {
  const origin = ORIGIN[from];
  const api = useApi();
  const { actions, setActions } = useTenant();
  const task = useTask();
  const [draft, setDraft] = useState<ActionDraft | null>(null);

  async function create(submit: ActionFormSubmit): Promise<void> {
    if (submit.intent !== "create") return;
    const ok = await task.run(async () => {
      const created = await api.call("actionCreate", { body: submit.body });
      setActions([...actions, created]);
    }, `Created "${submit.body.spec.name}".`);
    if (ok) navigate(origin.path);
  }

  if (draft !== null) {
    return (
      <>
        <button type="button" className="back-link" onClick={() => { setDraft(null); }}>
          <Icon name="arrowLeft" size={14} /> Choose again
        </button>
        <PageHead title={`New ${ACTION_TYPE_LABEL[draft.type].toLowerCase()} action`} />
        <ActionForm initial={draft} intent="create" busy={task.busy} onSubmit={(submit) => void create(submit)} onCancel={() => { navigate(origin.path); }} />
      </>
    );
  }

  return (
    <>
      <Link to={origin.path} className="back-link">
        <Icon name="arrowLeft" size={14} /> {origin.label}
      </Link>
      <PageHead title="New action" />
      <div className="choice-grid">
        {TYPE_CHOICES.map((choice) => (
          <button key={choice.type} type="button" className="choice" onClick={() => { setDraft(emptyDraft(choice.type)); }}>
            <Badge tone="outline">{ACTION_TYPE_LABEL[choice.type]}</Badge>
            <span className="choice-text">{choice.blurb}</span>
            <Icon name="arrowRight" size={16} className="choice-arrow" />
          </button>
        ))}
      </div>
      <Eyebrow>Templates</Eyebrow>
      <TemplatePicker onPick={setDraft} />
    </>
  );
}

export function TemplatePicker({ onPick }: { onPick: (draft: ActionDraft) => void }) {
  const api = useApi();
  const { resource, reload } = useResource(async () => api.call("actionTemplates", {}), [api]);
  if (resource.status === "loading") return <Spinner />;
  if (resource.status === "error") return <ErrorNote message={resource.message} onRetry={() => void reload()} />;
  return (
    <div className="choice-grid choice-grid-templates">
      {resource.data.templates.map((template) => (
        <button key={template.key} type="button" className="choice" onClick={() => { onPick(draftFromTemplate(template.spec)); }}>
          <span className="choice-title">{template.title}</span>
          <span className="choice-text">{template.blurb}</span>
          <Badge tone="outline">{ACTION_TYPE_LABEL[template.spec.type]}</Badge>
        </button>
      ))}
    </div>
  );
}
